// @ts-strict-ignore
import * as React from 'react';
import { runwayColors } from '../styles/globalStyles';
import { InfoOutlined } from '@ant-design/icons';
import { FeatureSection } from '../../typescriptHelpers';
import { InfoView } from './InfoView';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectIsReleaseFromScraper,
    selectHasRequiredIntegrationsWithCapabilitiesInstalledForReleaseStep,
    selectIsReleaseCompleted,
} from '../../selectors/selectors';
import Tooltip, { TooltipPlacement } from 'antd/lib/tooltip';
import { ReleaseStepType, StatisticCategory, StatisticScope } from '../../types';

const INFO_ICON_SIZE = 60;

const Overlay: React.FC<{ shouldCenterContent: boolean }> = ({ children, shouldCenterContent }) => {
    return (
        <div
            className="empty-state-wrapper-content"
            style={{
                borderRadius: 6,
                position: 'absolute',
                backdropFilter: 'blur(0px)',
                opacity: 0.0,
                transition: 'all 0.3s',
                backgroundColor: 'var(--rw-overlay-bg)',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                zIndex: 1,
                ...(shouldCenterContent ?
                    {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }
                :   {}),
            }}
        >
            {children}
        </div>
    );
};

const PulsatingDot: React.FC<{ shouldHideDotOnHover: boolean }> = ({ shouldHideDotOnHover }) => {
    return (
        <div
            className={shouldHideDotOnHover ? 'empty-state-wrapper-pulsating-dot' : null}
            style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'none',
                opacity: 1,
                transition: 'opacity 0.3s',
            }}
        >
            <div
                style={{
                    width: INFO_ICON_SIZE,
                    height: INFO_ICON_SIZE,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        width: 21,
                        height: 21,
                        backgroundColor: runwayColors.primary,
                        borderRadius: 30,
                        animationName: 'pulsating-info',
                        animationDuration: '2s',
                        animationIterationCount: 'infinite',
                        top: '50%',
                        left: '50%',
                        zIndex: -1,
                        transform: 'translate(-50%, -50%)',
                    }}
                />
                <div
                    style={{
                        width: 21,
                        height: 21,
                        backgroundColor: runwayColors.primary,
                        borderRadius: 30,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <InfoOutlined
                        style={{
                            margin: 0,
                            color: runwayColors.white,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export const useIsInEmptyState = (releaseId: string, appId: string, releaseStepType: ReleaseStepType) => {
    const isReleaseFromScraper = useSelector((state: RootState) =>
        releaseId ? selectIsReleaseFromScraper(state, releaseId) : false,
    );

    const isReleaseComplete = useSelector((state: RootState) =>
        releaseId ? selectIsReleaseCompleted(state, releaseId) : false,
    );

    const hasRequiredCapabilities = useSelector((state: RootState) =>
        releaseStepType ?
            selectHasRequiredIntegrationsWithCapabilitiesInstalledForReleaseStep(state, appId, releaseStepType, true)
        :   true,
    );

    return isReleaseFromScraper || (!!releaseId && !hasRequiredCapabilities && isReleaseComplete);
};

export interface EmptyStateWrapperProps {
    children?: React.ReactNode | ((isInEmptyState: boolean) => React.ReactNode);
    appId: string;
    releaseId: string;
    shouldForceDisable?: boolean;
    shouldForceEnable?: boolean;
    placement?: TooltipPlacement;
    featureSections: FeatureSection[];
    shouldClipContent?: boolean;
    shouldShowAsTooltip?: boolean;
    shouldHideDotOnHover?: boolean;
    shouldCenterContent?: boolean;
    customWrapperStyle?: React.CSSProperties;
    releaseStepType?: ReleaseStepType;
    extraInfo?: {
        scope?: StatisticScope;
    };
}

export const EmptyStateWrapper: React.FC<EmptyStateWrapperProps> = ({
    children = null,
    shouldForceDisable = false,
    shouldForceEnable = false,
    shouldClipContent = false,
    shouldShowAsTooltip = false,
    shouldHideDotOnHover = true,
    shouldCenterContent = false,
    customWrapperStyle = {},
    featureSections,
    placement,
    releaseId,
    appId,
    releaseStepType,
    extraInfo,
}) => {
    const [height, setHeight] = React.useState(0);
    const ref = React.useRef(null);
    const domRoot = React.useRef<HTMLBaseElement>(null);

    React.useLayoutEffect(() => {
        const height = ref?.current?.clientHeight;
        if (height) {
            setHeight(height);
        }
    }, []);

    // TODO: leaving this in here so we can turn it back on if we have a better strategy for determining when to
    // show the treatment for steps that don't have required capabilities
    // let isEnabled = isReleaseFromScraper || !hasRequiredCapabilities;
    let isEnabled = useIsInEmptyState(releaseId, appId, releaseStepType);

    const processedChildren: React.ReactNode = typeof children === 'function' ? children(isEnabled) : children;

    if (shouldForceDisable) {
        isEnabled = false;
    }

    // shouldForceEnable takes precedence over shouldForceDisable
    if (shouldForceEnable) {
        isEnabled = true;
    }

    if (!isEnabled) {
        // don't do anything, just return the children
        return <>{processedChildren}</>;
    }

    if (shouldShowAsTooltip || !processedChildren) {
        const defaultChildren = (
            <div
                style={{
                    position: 'relative',
                    width: INFO_ICON_SIZE,
                    height: INFO_ICON_SIZE,
                }}
            />
        ) as React.ReactNode;

        const contents = React.Children.map(processedChildren ?? defaultChildren, (child) => {
            if (React.isValidElement(child)) {
                return React.cloneElement(
                    child as React.ReactElement,
                    {
                        style: {
                            ...child.props.style,
                            position: 'relative',
                            ...(shouldClipContent ? { overflow: 'hidden' } : {}),
                            ...customWrapperStyle,
                        } as React.CSSProperties,
                        ref: domRoot,
                    },
                    <>
                        <div
                            style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                zIndex: 1,
                                top: 0,
                                left: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Tooltip
                                overlayStyle={{
                                    maxWidth: 800,
                                }}
                                placement={placement}
                                arrow={{ pointAtCenter: true }}
                                overlayInnerStyle={{ width: 540, padding: '14px 20px' }}
                                title={<InfoView featureSections={featureSections} appId={appId} />}
                            >
                                <span
                                    className="empty-state-wrapper"
                                    style={{
                                        position: 'relative',
                                        width: INFO_ICON_SIZE,
                                        height: INFO_ICON_SIZE,
                                    }}
                                >
                                    <PulsatingDot shouldHideDotOnHover={shouldHideDotOnHover} />
                                </span>
                            </Tooltip>
                        </div>
                        {child.props.children}
                    </>,
                );
            }
            return child;
        });

        return <>{contents}</>;
    }

    const contents = React.Children.map(processedChildren, (child) => {
        if (React.isValidElement(child)) {
            return React.cloneElement(
                child as React.ReactElement,
                {
                    style: {
                        ...child.props.style,
                        position: 'relative',
                        borderRadius: 6,
                        ...(height ? { minHeight: height } : {}),
                        ...(shouldClipContent ? { overflow: 'hidden' } : {}),
                        ...customWrapperStyle,
                    } as React.CSSProperties,
                    className: 'empty-state-wrapper',
                    ref: domRoot,
                },
                <>
                    <Overlay shouldCenterContent={shouldCenterContent}>
                        <div
                            ref={ref}
                            style={{
                                ...(shouldCenterContent ?
                                    { maxWidth: 540, border: `1px solid ${runwayColors.white}`, borderRadius: 6 }
                                :   {}),
                                padding: '14px 20px',
                            }}
                        >
                            <InfoView featureSections={featureSections} appId={appId} extraInfo={extraInfo} />
                        </div>
                    </Overlay>
                    <PulsatingDot shouldHideDotOnHover={shouldHideDotOnHover} />
                    {child.props.children}
                </>,
            );
        }
        return child;
    });

    return <>{contents}</>;
};

export const featureSectionForStatisticCategory: (category: StatisticCategory) => FeatureSection = (category) => {
    switch (category) {
        case StatisticCategory.DevOps:
            return FeatureSection.OrgOverviewMobileDevOpsMetrics;
        case StatisticCategory.Work:
            return FeatureSection.OrgOverviewWorkMetrics;
        case StatisticCategory.Build:
            return FeatureSection.OrgOverviewBuildMetrics;
        case StatisticCategory.StabilityAndHealth:
            return FeatureSection.OrgOverviewStabilityHealthMetrics;
    }

    return null;
};
